import { Component, Input, OnInit } from '@angular/core';
import { ActiveWidget } from '../../classes/active-widget';
import { WidgetModel } from '../../models/widget.model';
import { StreamWidgetInterface } from '../../interfaces/stream-widget.interface';
import { ActivityModel } from '@kaiser-software/chassis-core/src/app/activities/models/activity.model';
import moment from "moment-es6";
import { LoaderInstance } from '../../../support/loading/loader.service';

@Component({
    selector: 'chassis-core-activity-stream-widget',
    templateUrl: './activity-stream-widget.component.html',
    styleUrls: ['./activity-stream-widget.component.scss'],
})
export class ActivityStreamWidgetComponent implements OnInit {

    @Input() public widget: WidgetModel;

    /**
     * The options
     *
     * @type {ListWidgetOptions}
     */
    @Input() public options: StreamWidgetInterface;

    /**
     * The collections of items.
     *
     * @type {Collection}
     */
    public activities: Array<{ period: string, activities: Array<ActivityModel> }> = [];

    /**
     * The current page of the stream
     *
     * @type {number}
     */
    public page = 1;

    /**
     * Whether there are more activities to load or not
     */
    public hasNextPage = false;

    /**
     * The LoaderInstance instance
     *
     * @type {LoaderInstance}
     */
    @Input() public loader: LoaderInstance;

    constructor(public widgetRef: ActiveWidget) {
    }

    ngOnInit() {
        this.loadItems();
    }

    /**
     * Load items
     *
     * @param {number} page
     * @returns {void}
     */
    public loadItems(page: number = this.page) {
        this.loader.start();
        if (!this.loader.isLoading()) {
            this.loader.start();
        }

        this.options.service.paginate(page, this.options.per_page, {
            orderBy: 'id',
            sortedBy: 'desc'
        }).subscribe(activities => {
            if (activities.count()) {
                this.addActivities(activities);

                this.page++;
            }

            this.hasNextPage = activities.count() === this.options.per_page;

            this.loader.stop();
        }, error => {
            console.log('Error: ', error);

            this.loader.stop();
        });
    }

    /**
     * Add the activities to the collection
     *
     * @param activities
     * @returns {void}
     */
    protected addActivities(activities) {
        activities.forEach(activity => {
            const period      = this.getActivityPeriod(activity.created_at);
            activity.changes  = this.parseChanges(activity.changes);
            activity.log_name = this.parseLogName(activity.log_name);

            const group = this.activities.find(activityGroup => activityGroup.period === period);

            if (group) {
                group.activities.push(activity);
            } else {
                this.activities.push({
                    period: period,
                    activities: [activity]
                });
            }
        });
    }

    /**
     * Receive the period of the activity
     *
     * @param created_at
     * @returns {string}
     */
    protected getActivityPeriod(created_at) {
        const date = moment(created_at);

        const daysAgo   = moment().diff(date, 'days');
        const weeksAgo  = moment().diff(date, 'weeks');
        const monthsAgo = moment().diff(date, 'months');
        const yearsAgo  = moment().diff(date, 'years');

        if (daysAgo < 1) {
            return 'Today';
        } else if (daysAgo <= 7) {
            return daysAgo + ' days ago';
        } else if (weeksAgo > 0 && monthsAgo < 1) {
            return weeksAgo + ' weeks ago';
        } else if (monthsAgo > 0 && yearsAgo < 1) {
            return monthsAgo + ' months ago';
        } else if (yearsAgo > 0) {
            return yearsAgo + ' years ago';
        } else {
            return 'no period found?';
        }
    }

    /**
     * Parse the changes of the activity
     *
     * @param changes
     * @returns {array}
     */
    private parseChanges(changes) {
        const parsedChanges = [];

        for (const field in changes.old) {
            if (changes.old.hasOwnProperty(field)) {
                parsedChanges.push({
                    field: field,
                    old: changes.old[field],
                    new: changes.attributes[field]
                });
            }
        }

        return parsedChanges;
    }

    /**
     *
     *
     * @param logName
     * @return {string}
     */
    private parseLogName(logName: string): string {
        logName = logName.replace(/-/g, ' ');

        return logName;
    }

    /**
     * Refresh the widget
     *
     * @returns {void}
     */
    public refresh() {
        this.widgetRef.refresh();

        this.activities  = [];
        this.page        = 1;
        this.hasNextPage = true;

        this.loadItems();
    }

    /**
     * Remove the widget
     *
     * @returns {void}
     */
    public remove() {
        this.widgetRef.remove();
    }

    /**
     * Indicates if the widget is loading
     *
     * @returns {void}
     */
    public get loading() {
        return this.loader ? this.loader.isLoading() : false;
    }
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/dashboards/components/activity-stream-widget/activity-stream-widget.component.ts